import { call, put, select, takeEvery } from 'redux-saga/effects';
import { CommentType, CommentsMode, PartialCommentType, PostType, PostUploadType } from 'types/postTypes';
import {
  GetCommentsRequestType,
  GetPostsRequestType,
  LikeCommentRequestType,
  LikePostRequestType,
  NtfCommentsRequestType,
  UpdatePostType,
  UploadFileType,
  createPostFailed,
  createPostSuccess,
  deleteCommentFailed,
  deleteCommentSuccess,
  deletePostFailed,
  deletePostSuccess,
  getCommentsFailed,
  getCommentsSuccess,
  getPostFailed,
  getPostSuccess,
  getPostsFailed,
  getPostsSuccess,
  likeCommentFailed,
  likeCommentSuccess,
  likePostFailed,
  likePostSuccess,
  linkFilesFailed,
  linkFilesSuccess,
  sendCommentFailed,
  sendCommentSuccess,
  setNtfCommentsStatusFailed,
  setNtfCommentsStatusSuccess,
  unlinkFilesFailed,
  unlinkFilesSuccess,
  updatePostFailed,
  updatePostSuccess,
  uploadPostFileFailed,
  uploadPostFileSuccess,
} from '../actions/postActions';
import {
  CREATE_POST,
  DELETE_COMMENT,
  DELETE_POST,
  GET_COMMENTS,
  GET_POST,
  GET_POSTS,
  LIKE_COMMENT,
  LIKE_POST,
  LINK_FILES,
  POST_COMMENT,
  SET_NTF_STATUS,
  UNLINK_FILES,
  UPDATE_COMMENT,
  UPDATE_POST,
  UPLOAD_POST_FILE,
} from '../constants';

import { cls } from 'constants/cls';
import { updateFeedPostReactions } from 'redux/actions/feedActions';
import { uploadFile } from 'redux/utils/uploadUtils';
import safeFetch from 'shared/api/safeFetch';
import config from 'shared/config';
import { cCommentStatusEnum } from 'types/clsType';
import { FileDescriptor } from 'types/fileTypes';
import { RootState } from 'types/stateTypes';
import { ActionType } from '../actions/themeActions';

const { apiUrl } = config;
type LinkFilesType = { postId: string; fileIds?: string[] };

function* createPostHandler(action: ActionType<PostUploadType>) {
  const { auth }: RootState = yield select();
  const { id: userId } = auth;
  const post = action.payload;
  const postData = {
    cCommentStatus: cCommentStatusEnum.allow,
    cStatus: '25:1',
    publicationDate: new Date().toISOString(),
    rAuthor: `${cls.user}:${userId}`,
    ...post,
  };
  try {
    const response: Response = yield call(fetch, `${apiUrl}/post`, {
      body: JSON.stringify(postData),
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
    });
    if (response.status !== 201) {
      const parsed: ResponseType = yield call([response, 'json']);
      throw parsed;
    } else {
      const uploadedPost: PostType = yield call([response, 'json']);
      const fileIds = post.files?.map((file) => file?.id);
      if (fileIds?.length) {
        const payload = { fileIds: fileIds as string[], postId: uploadedPost.id };
        yield call(linkFilesHandler, { payload, type: LINK_FILES });
      }
      yield put(createPostSuccess({}));
    }
  } catch (error) {
    console.error('createPostError', error);
    createPostFailed(error);
  }
}

function* updatePostHandler(action: ActionType<UpdatePostType>) {
  const { files, filesToRemove, post } = action.payload;
  const { id, text, title } = post;
  const newFilesIds = files.map((file) => file?.id).filter((fileId) => typeof fileId === 'string' && fileId);
  const removeFilesIds = filesToRemove?.map((file) => file.id).filter((fileId) => typeof fileId === 'string' && fileId);
  try {
    const response: Response = yield call(fetch, `${apiUrl}/post`, {
      body: JSON.stringify({ id, text, title }),
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'PATCH',
    });
    if (response.status === 200) {
      if (newFilesIds.length) {
        const payload = { fileIds: newFilesIds as string[], postId: id };
        yield call(linkFilesHandler, { payload, type: LINK_FILES });
      }
      if (removeFilesIds.length) {
        const payload = { fileIds: removeFilesIds as string[], postId: id };
        yield call(unlinkFilesHandler, { payload, type: LINK_FILES });
      }
      yield put(updatePostSuccess(post));
    } else {
      throw response;
    }
  } catch (error) {
    console.error('updatePostError', error);
    yield put(updatePostFailed(error));
  }
}

function* deletePostHandler(action: ActionType<string>) {
  const postId = action.payload;
  try {
    yield call(fetch, `${apiUrl}/post/${postId}`, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'DELETE',
    });
    yield put(deletePostSuccess(action.payload));
  } catch (error) {
    console.error('deletePostError', error);
    yield put(deletePostFailed(error));
  }
}

function* getPostHandler(action: ActionType<string>) {
  const postId = action.payload;
  try {
    const { data } = yield call(safeFetch, `${apiUrl}/post/${postId}`, {
      headers: { access: '*/*' },
      method: 'GET',
    });
    yield put(getPostSuccess(data));
  } catch (error) {
    console.error('getPostError', error);
    yield put(getPostFailed(error));
  }
}

function* getPostsHandler(action: ActionType<GetPostsRequestType>) {
  const { asc, pageSize, sinceDateTime, isAppend } = action.payload;
  const body = {
    asc: asc || false,
    pageSize: pageSize || 20,
    sinceDateTime: sinceDateTime,
  };
  try {
    const { data } = yield call(safeFetch, `${apiUrl}/post/fetch`, {
      body,
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'POST',
    });
    yield put(getPostsSuccess({ hasMore: data.length === body.pageSize, isAppend, posts: data as PostType[] }));
  } catch (error) {
    console.error('getPostsError', error);
    yield put(getPostsFailed(error));
  }
}

function* uploadPostFileHandler(action: ActionType<UploadFileType>) {
  try {
    const { formData, fileInfo } = action.payload;
    const { data } = yield call(uploadFile, formData);
    let mergedData = data;
    if (data && data.length && fileInfo && fileInfo.length === data.length) {
      mergedData = data.map((fileDescriptor: FileDescriptor, index: number) => ({
        ...fileDescriptor,
        fileType: fileInfo[index].fileType,
      }));
    }
    yield put(uploadPostFileSuccess({ data: mergedData }));
  } catch (error) {
    yield put(uploadPostFileFailed(error));
  }
}

function* linkFilesHandler({ payload }: ActionType<LinkFilesType>) {
  const { postId, fileIds } = payload;
  try {
    const uploadFileResponse: Response = yield call(fetch, `${apiUrl}/s/post/${postId}/file_default/link`, {
      body: JSON.stringify({ files: fileIds }),
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
    });
    if (uploadFileResponse.status !== 200) {
      const parsed: ResponseType = yield call([uploadFileResponse, 'json']);
      throw parsed;
    } else {
      yield put(linkFilesSuccess({}));
    }
  } catch (error) {
    console.error('linkFileError', error);
    linkFilesFailed(error);
  }
}

function* unlinkFilesHandler({ payload }: ActionType<LinkFilesType>) {
  const { postId, fileIds } = payload;
  try {
    const unlinkFilesResponse: Response = yield call(fetch, `${apiUrl}/s/post/${postId}/unlink`, {
      body: JSON.stringify({ deleteFiles: true, files: fileIds }),
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'DELETE',
    });
    if (unlinkFilesResponse.status !== 200) {
      const parsed: ResponseType = yield call([unlinkFilesResponse, 'json']);
      throw parsed;
    } else {
      yield put(unlinkFilesSuccess({}));
    }
  } catch (error) {
    console.error('unlinkFilesError', error);
    unlinkFilesFailed(error);
  }
}

function* getCommentsHandler(action: ActionType<GetCommentsRequestType>) {
  const { asc, pageSize, postId, sinceDateTime, isAppend, up, view, mode } = action.payload;
  const body = {
    asc: asc || false,
    mode,
    pageSize: pageSize || 10,
    postId,
    sinceDateTime: sinceDateTime,
    up,
    view: view || 'tree',
  };
  try {
    const { data }: { data: CommentType[] } = yield call(safeFetch, `${apiUrl}/post/comment/fetch`, {
      body: body,
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'POST',
    });
    yield put(
      getCommentsSuccess({
        commentId: up,
        comments: data,
        hasMore: data.length === body.pageSize,
        isAppend,
        mode: mode || CommentsMode.inOrder,
      })
    );
  } catch (error) {
    console.error('getCommentsError', error);
    yield put(getCommentsFailed(error));
  }
}

function* sendCommentHandler(action: ActionType<PartialCommentType>) {
  const { cStatus, rAuthor, rPost, text, up } = action.payload;
  const body = { cStatus: cStatus || '25:1', rAuthor: `202:${rAuthor}`, rPost: `302:${rPost}`, text, up };
  try {
    const { data } = yield call(safeFetch, `${apiUrl}/post/comment`, {
      body,
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'POST',
    });
    yield put(sendCommentSuccess(data as unknown as CommentType));
  } catch (error) {
    console.error('sendCommentError', error);
    yield put(sendCommentFailed(error));
  }
}

function* editCommentHandler(action: ActionType<PartialCommentType>) {}

function* removeCommentHandler(action: ActionType<CommentType>) {
  const { id: commentId } = action.payload;
  try {
    yield call(safeFetch, `${apiUrl}/post/comment/${commentId}`, {
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'DELETE',
    });
    yield put(deleteCommentSuccess(action.payload));
  } catch (error) {
    console.error('deleteCommentError', error);
    yield put(deleteCommentFailed(error));
  }
}

function* likeCommentHandler(action: ActionType<LikeCommentRequestType>) {
  const { commentId, actionType } = action.payload;
  const body = actionType === 'like' ? '91:1' : undefined;
  try {
    yield call(safeFetch, `${apiUrl}/post/comment/${commentId}/react`, {
      body,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'PUT',
    });
    yield put(likeCommentSuccess(commentId));
  } catch (error) {
    console.error('likeCommentFailed', error);
    yield put(likeCommentFailed(error));
  }
}

function* likePostHandler(action: ActionType<LikePostRequestType>) {
  const { postId, actionType } = action.payload;
  const body = actionType === 'like' ? '91:1' : undefined;
  try {
    yield call(safeFetch, `${apiUrl}/post/${postId}/react`, {
      body,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'PUT',
    });
    yield put(likePostSuccess(postId));
    yield put(updateFeedPostReactions(postId));
  } catch (error) {
    console.error('likePostFailed', error);
    yield put(likePostFailed(error));
  }
}

function* setNtfCommentsStatusHandler(action: ActionType<NtfCommentsRequestType>) {
  const { uref, ntfStatus } = action.payload;
  try {
    yield call(safeFetch, `${apiUrl}/notification/r_object/${uref}/comment_status`, {
      body: {
        value: ntfStatus,
      },
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'PUT',
    });
    yield put(setNtfCommentsStatusSuccess(action.payload));
  } catch (error) {
    yield put(setNtfCommentsStatusFailed(error));
  }
}

export default function* fetcPostWatcher(): Generator {
  yield takeEvery(GET_COMMENTS, getCommentsHandler);
  yield takeEvery(POST_COMMENT, sendCommentHandler);
  yield takeEvery(UPDATE_COMMENT, editCommentHandler);
  yield takeEvery(DELETE_COMMENT, removeCommentHandler);
  yield takeEvery(LINK_FILES, linkFilesHandler);
  yield takeEvery(UPLOAD_POST_FILE, uploadPostFileHandler);
  yield takeEvery(UNLINK_FILES, unlinkFilesHandler);
  yield takeEvery(CREATE_POST, createPostHandler);
  yield takeEvery(UPDATE_POST, updatePostHandler);
  yield takeEvery(DELETE_POST, deletePostHandler);
  yield takeEvery(GET_POST, getPostHandler);
  yield takeEvery(GET_POSTS, getPostsHandler);
  yield takeEvery(LIKE_COMMENT, likeCommentHandler);
  yield takeEvery(LIKE_POST, likePostHandler);
  yield takeEvery(SET_NTF_STATUS, setNtfCommentsStatusHandler);
}
